import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { isAfter, parseISO, startOfWeek } from 'date-fns';
import {
  GetPendingTimeSlotsArgs,
  GetPendingTimeSlotsRejectedResponse,
  GetPendingTimeSlotsResponse,
  GetTimeSlotsArgs,
  GetTimeSlotsPayload,
  TimeSlotsState,
} from './timeSlotsTypes';
import {
  AvailableTimeslotsResponse,
  Slot,
} from '../../services/Appointment/model';
import {
  isTimeInBrowserSettings,
  ServiceLocation,
  startOfWeekDayToNumber,
} from '../../helpers';
import { services } from '../../context';
import { getInitialSessionOnClientEntry } from '../../helpers/sessionHelper';
import { isFeatureEnabled } from '../../helpers/featureHelper';
import {
  markSelectedSlotAsCompleted,
  toSlots,
} from '../../services/Appointment/utils';

const MAX_TIME_SLOTS_IN_RESPONSE = CSS_CONFIG.SERVICES.maxTimeSlotsInResponse;
const WEEK_PERIOD = 7;

const initialState = (): TimeSlotsState => {
  const restoredState = getInitialSessionOnClientEntry(SUB_PATH);
  return {
    from: restoredState.from || new Date().toISOString(),
    prevEnabled: false,
    availableTimeSlots: {},
    replacementServiceAvailableTimeSlots: {},
    pendingSlots: {
      isUpdating: false,
      activeUpdatingCount: 0,
    },
  };
};

async function fetchAndAggregateTimeSlots(
  zipCode: string,
  serviceId: string,
  location: ServiceLocation,
  storeId: string,
  from: string,
  serviceCode: string,
  weekStartsOn: 0 | 1,
): Promise<{ allTimeSlots: AvailableTimeslotsResponse[]; error: boolean }> {
  const requests = Array.from({ length: 4 }, (_, i) => {
    const weekStart = startOfWeek(parseISO(from), { weekStartsOn });
    weekStart.setDate(weekStart.getDate() + i * 7);
    return services.appointment.getAvailableTimeslots(
      zipCode,
      serviceId,
      location,
      storeId,
      weekStart.toISOString(),
      serviceCode,
      WEEK_PERIOD,
    );
  });

  const results = await Promise.all(requests);

  const allTimeSlots: AvailableTimeslotsResponse[] = [];
  let error = false;
  for (const res of results) {
    if (res.error) {
      error = true;
    }
    if (res.result && Array.isArray(res.result)) {
      allTimeSlots.push(...res.result);
    }
  }

  return { allTimeSlots, error };
}

export const getPendingTimeSlots = createAsyncThunk<
  GetPendingTimeSlotsResponse,
  GetPendingTimeSlotsArgs,
  { rejectValue: GetPendingTimeSlotsRejectedResponse }
>(
  'timeSlots/getPendingTimeSlots',
  async (
    {
      zipCode,
      serviceId,
      location,
      storeId,
      from,
      serviceCode,
      isReplacementService,
      numberOfDays,
      pendingSelectedSlot,
    },
    { rejectWithValue },
  ) => {
    const fromDate = parseISO(from).toISOString();

    if (!location) {
      return rejectWithValue({
        storeId,
        from: fromDate,
        isReplacementService,
      });
    }

    const { result, error } = await services.appointment.getAvailableTimeslots(
      zipCode,
      serviceId,
      location,
      storeId,
      fromDate,
      serviceCode,
      numberOfDays,
    );
    if (error) {
      return rejectWithValue({
        storeId,
        from: fromDate,
        isReplacementService,
      });
    }

    const slots: Slot[] = toSlots(
      result,
      isTimeInBrowserSettings(location, CSS_CONFIG),
      fromDate,
      [],
      result.length === MAX_TIME_SLOTS_IN_RESPONSE,
      CSS_CONFIG,
    );

    markSelectedSlotAsCompleted(slots, pendingSelectedSlot);

    return {
      storeId,
      slots,
      isReplacementService,
    };
  },
);

export const getTimeSlots = createAsyncThunk<
  GetTimeSlotsPayload,
  GetTimeSlotsArgs
>(
  'timeSlots/getTimeSlots',
  async ({
    zipCode,
    serviceId,
    location,
    storeId,
    from,
    serviceCode,
    isReplacementService,
    zipCodeForLocation,
    isExternalService,
  }) => {
    const weekStartsOn = startOfWeekDayToNumber(
      CSS_CONFIG.FORMATS.firstDayOfWeek,
    );
    const firstDayOfWeek = startOfWeek(parseISO(from), { weekStartsOn });
    const firstDayOfWeekISOstring = firstDayOfWeek.toISOString();
    const prevEnabled = isAfter(firstDayOfWeek, new Date());
    let availableTimeSlots: AvailableTimeslotsResponse[] = [];
    const { splitTimeSlotRequest } = CSS_CONFIG.SERVICES;

    if (splitTimeSlotRequest.includes(serviceId)) {
      const { allTimeSlots, error } = await fetchAndAggregateTimeSlots(
        zipCode,
        serviceId,
        location,
        storeId,
        from,
        serviceCode,
        weekStartsOn,
      );
      availableTimeSlots = allTimeSlots;
      if (error) {
        return Promise.reject();
      }
    } else {
      const { result, error } =
        await services.appointment.getAvailableTimeslots(
          zipCode,
          serviceId,
          location,
          storeId,
          firstDayOfWeekISOstring,
          serviceCode,
        );
      availableTimeSlots = result;
      if (error) {
        return Promise.reject();
      }
    }

    const totalTimeSlots = availableTimeSlots.length;
    if (totalTimeSlots === 0) {
      return {
        storeId,
        from: firstDayOfWeekISOstring,
        prevEnabled,
        slots: [],
        isReplacementService,
      };
    }

    const capacity =
      !isExternalService &&
      isFeatureEnabled('capacityEndpoint') &&
      totalTimeSlots === MAX_TIME_SLOTS_IN_RESPONSE
        ? await services.appointment.getTimeSlotCapacity(
            zipCodeForLocation,
            serviceId,
            firstDayOfWeekISOstring,
            isTimeInBrowserSettings(location, CSS_CONFIG),
          )
        : [];

    const slots: Slot[] = toSlots(
      availableTimeSlots,
      isTimeInBrowserSettings(location, CSS_CONFIG),
      firstDayOfWeekISOstring,
      capacity,
      !!capacity.length,
      CSS_CONFIG,
      isReplacementService,
    );

    return {
      storeId,
      from: firstDayOfWeekISOstring,
      prevEnabled,
      slots,
      isReplacementService,
    };
  },
);

const timeSlotsSlice = createSlice({
  name: 'timeSlots',
  initialState: initialState(),
  reducers: {
    clearAvailableTimeSlots(state) {
      state.availableTimeSlots = {};
      state.replacementServiceAvailableTimeSlots = {};
    },
    resetTimeSlotsFromDate(state) {
      state.from = new Date().toISOString();
    },
    clearSlotErrors(state) {
      Object.values(state.availableTimeSlots).forEach((slotsObject) => {
        slotsObject.forEach((slot) => {
          if (slot.status === 'error') {
            slot.status = 'pending';
          }
        });
      });
      Object.values(state.replacementServiceAvailableTimeSlots).forEach(
        (slotsObject) => {
          slotsObject.forEach((slot) => {
            if (slot.status === 'error') {
              slot.status = 'pending';
            }
          });
        },
      );
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      getTimeSlots.fulfilled,
      (state, action: PayloadAction<GetTimeSlotsPayload>) => {
        state.from = action.payload.from;
        state.prevEnabled = action.payload.prevEnabled;
        if (action.payload.isReplacementService) {
          state.replacementServiceAvailableTimeSlots = {
            [action.payload.storeId]: action.payload.slots,
          };
        } else {
          state.availableTimeSlots = {
            [action.payload.storeId]: action.payload.slots,
          };
        }
      },
    );
    builder.addCase(
      getPendingTimeSlots.fulfilled,
      (
        state,
        action: PayloadAction<{
          storeId: string;
          slots: Slot[];
          isReplacementService?: boolean;
        }>,
      ) => {
        const activeUpdatingCount = state.pendingSlots.activeUpdatingCount - 1;
        state.pendingSlots = {
          isUpdating: activeUpdatingCount > 0,
          activeUpdatingCount,
        };
        if (action.payload.isReplacementService) {
          state.replacementServiceAvailableTimeSlots = {
            ...state.replacementServiceAvailableTimeSlots,
            [action.payload.storeId]:
              state.replacementServiceAvailableTimeSlots[
                action.payload.storeId
              ].map((existingSlot) => {
                const updatedSlot = action.payload.slots.find(
                  (slot) => slot.date === existingSlot.date,
                );
                return updatedSlot || existingSlot;
              }),
          };
        } else {
          state.availableTimeSlots = {
            ...state.availableTimeSlots,
            [action.payload.storeId]: state.availableTimeSlots[
              action.payload.storeId
            ].map((existingSlot) => {
              const updatedSlot = action.payload.slots.find(
                (slot) => slot.date === existingSlot.date,
              );
              return updatedSlot || existingSlot;
            }),
          };
        }
      },
    );
    builder.addCase(getPendingTimeSlots.rejected, (state, action) => {
      const activeUpdatingCount = state.pendingSlots.activeUpdatingCount - 1;
      state.pendingSlots = {
        isUpdating: activeUpdatingCount > 0,
        activeUpdatingCount,
      };

      if (!action.payload) return;
      const { storeId, from, isReplacementService } = action.payload;

      if (isReplacementService) {
        const slotFound = state.replacementServiceAvailableTimeSlots[
          storeId
        ].find((slot) => slot.date === from);
        if (slotFound) {
          slotFound.status = 'error';
        }
      } else {
        const slotFound = state.availableTimeSlots[storeId].find(
          (slot) => slot.date === from,
        );
        if (slotFound) {
          slotFound.status = 'error';
        }
      }
    });
    builder.addCase(getPendingTimeSlots.pending, (state) => {
      state.pendingSlots = {
        isUpdating: true,
        activeUpdatingCount: state.pendingSlots.activeUpdatingCount + 1,
      };
    });
  },
});

export const {
  clearAvailableTimeSlots,
  resetTimeSlotsFromDate,
  clearSlotErrors,
} = timeSlotsSlice.actions;

export default timeSlotsSlice.reducer;
